import React, { useCallback, useEffect, useRef, useState } from "react";
import Config from "../../../utils/config";
import RequestV2 from "../../../api/RequestV2";
import {
  dateConvertWithoutTime,
  fromDateConvert,
  toDateConvert,
} from "../../../utils/common";
import { CsvDown } from "../../integrations/export";
import {
  Card,
  ListPageFilterBar,
  PageLoader,
  PageBodyTitle,
  StatusBadge,
  Table,
  Button,
} from "../../design-system";

const STATUS_OPTIONS = [
  { value: "PENDING", label: "PENDING" },
  { value: "COMPLETED", label: "COMPLETED" },
  { value: "FAILED", label: "FAILED" },
  { value: "PROCESSED", label: "PROCESSED" },
];

const FILTERS = [
  {
    name: "fromDate",
    type: "date",
    value: "",
    placeholder: "From Date",
  },
  {
    name: "toDate",
    type: "date",
    value: "",
    placeholder: "To Date",
  },
  {
    name: "status",
    type: "select",
    value: "",
    placeholder: "Select Status",
    options: STATUS_OPTIONS,
  },
];

function buildPayload(controls, pageNo, pageSize) {
  const get = (name) =>
    controls.find((c) => c.name === name)?.value ?? "";

  return {
    status: get("status") || "",
    fileName: get("fileName") || "",
    fileType: get("fileType") || "",
    fromDate: get("fromDate")
      ? dateConvertWithoutTime(fromDateConvert(get("fromDate")))
      : "",
    toDate: get("toDate")
      ? dateConvertWithoutTime(toDateConvert(get("toDate")))
      : "",
    pagination: {
      pageNo,
      pageSize,
    },
  };
}

export default function InventoryListV2() {
  const [controls, setControls] = useState([...FILTERS]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);

  const [downloadPayload, setDownloadPayload] = useState(() =>
    buildPayload(FILTERS, 1, 500)
  );

  const controlsRef = useRef(controls);
  controlsRef.current = controls;

  const fetchData = useCallback((controlValues, pageNo, size) => {
    setLoading(true);

    const payload = buildPayload(controlValues, pageNo, size);

    const api = new RequestV2(
      "",
      (data) => {
        setLoading(false);
        const total = data?.totalElements || 0;
        setRows(Array.isArray(data?.content) ? data.content : []);
        setTotalPages(data?.totalPages || 1);
        setTotalElements(data?.totalElements || 0);
        setDownloadPayload(buildPayload(controlValues, 1, total));
      },
      () => {
        setLoading(false);
        setRows([]);
      }
    );

    api.post(Config.apis.admin.INVENTORY_LIST, payload);
  }, []);
    const downloadInventory = (fileName) => {
        const api = new RequestV2(
            "",
            (response) => {
                const blob = new Blob([response]);

                const url = window.URL.createObjectURL(blob);

                const link = document.createElement("a");
                link.href = url;
                link.download = fileName;
                document.body.appendChild(link);
                link.click();

                link.remove();
                window.URL.revokeObjectURL(url);
            },
            () => { }
        );

        api.get(Config.apis.admin.DOWNLOAD_INVENTORY, {
            params: { fileName },
            responseType: "blob",
        });
    };

  useEffect(() => {
    fetchData(controls, currentPage, pageSize);
  }, [currentPage, pageSize]);

  const columns = [
    {
      key: "fileName",
      label: "File Name",
      render: (_, row) => row?.fileName ?? "—",
    },
    {
      key: "fileType",
      label: "File Type",
      render: (_, row) => row?.fileType?.toUpperCase() ?? "—",
    },
    {
      key: "fileSize",
      label: "File Size",
      render: (_, row) => row?.fileSize ?? "—",
    },
    {
      key: "createdAt",
      label: "Created At",
      render: (_, row) =>
          row?.createdAt
              ? new Date(row.createdAt).toLocaleString()
              : "—",
    },
    {
      key: "status",
      label: "Status",
      render: (_, row) =>
        row?.status ? (
          <StatusBadge status={row.status} />
        ) : (
          "—"
        ),
    },
    {
      key: "errorMsg",
      label: "Error Message",
      render: (_, row) => row?.errorMsg || "—",
    },
      {
          key: "fileUrl",
          label: "File",
          render: (_, row) =>
              row?.fileName ? (
                  <Button
                      size="xs"
                      variant="secondary"
                      onClick={() => downloadInventory(row.fileName)}
                  >
                      Download
                  </Button>
              ) : (
                  "—"
              ),
      },
  ];

  if (loading && rows.length === 0) {
    return <PageLoader />;
  }

  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 20,
          width: "100%",
        }}
      >
        <PageBodyTitle>
          Inventory Upload Listing
        </PageBodyTitle>

        <div style={{ marginLeft: "auto" }}>
                  <CsvDown
                      headers={[
                          { label: "File Name", key: "fileName" },
                          { label: "File Type", key: "fileType" },
                          { label: "File Size", key: "fileSize" },
                          { label: "Created At", key: "createdAt" },
                          { label: "Status", key: "status" },
                          { label: "Error Message", key: "errorMsg" },
                          { label: "File URL", key: "fileUrl" },
                      ]}
                      params={downloadPayload}
                      url={Config.apis.admin.INVENTORY_LIST}
                      reportName="inventory-list.csv"
                      useV2
                  />
        </div>
      </div>

      <Card>
        <ListPageFilterBar
          filters={controls}
          onFilterChange={(name, value) =>
            setControls((prev) =>
              prev.map((f) =>
                f.name === name
                  ? { ...f, value }
                  : f
              )
            )
          }
          onSearch={() => {
            setCurrentPage(1);
            fetchData(
              controlsRef.current,
              1,
              pageSize
            );
          }}
          onReset={() => {
            const reset = FILTERS.map((f) => ({
              ...f,
              value: "",
            }));

            setControls(reset);
            setCurrentPage(1);

            fetchData(reset, 1, pageSize);
          }}
        />

        <Table
          data={rows}
          columns={columns}
          rowKey={(row, idx) =>
            row?.id || row?.uuid || idx
          }
          loading={loading}
          emptyMessage="No inventory upload records found"
          pagination={{
            currentPage,
            pageSize,
            total:
              totalElements ||
              totalPages * pageSize,
            onPageChange: setCurrentPage,
            pageSizeOptions: [10, 20, 50],
            onPageSizeChange: (size) => {
              setPageSize(size);
              setCurrentPage(1);
            },
          }}
        />
      </Card>
    </>
  );
}